import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { adminRequest, hashFile, operationKey } from "lib/adminSiteApi";
import { createJournal, transfer, MAX_BYTES } from "lib/adminPaperwork";
import { ErrorNotice, Loading, useRemote } from "components/admin/workspace/Controls";

// Paperwork editions (website launch L2.5, owner only): one edition per Basic package × truck × arrangement × state.
// Write what it covers, upload its ZIP (checked automatically), then publish exactly one checked file.
const TRUCKS = [["box-truck", "Box Truck"], ["semi-dry-van", "Semi – Dry Van"], ["semi-reefer", "Semi Reefer"]];
const ARRANGEMENTS = [["w2", "W-2 drivers"], ["owner-operator-leased-on", "Owner-operators · Leased-on"], ["owner-operator-own-authority", "Owner-operators · Own authority"]];
const REASONS = {
  archive_content_type: "is a file type that cannot be sold (allowed: PDF, Word, Excel, CSV, TXT, PNG, JPG)",
  archive_pdf_active: "is a PDF with scripts, automatic actions or attached files",
  archive_pdf_unreadable: "is a PDF that cannot be read safely",
  archive_pdf_encrypted: "is a password-protected PDF",
  archive_office_active_content: "contains macros, embedded programs or self-updating fields",
  archive_office_external_link: "links to files outside the document",
  archive_office_structure: "is a Word or Excel file with an unexpected structure",
  archive_office_content: "is a Word or Excel file with an unexpected part",
  archive_csv_formula: "has a cell a spreadsheet would run as a formula",
  archive_image: "is a damaged picture or has extra data after the picture",
  archive_path: "has an unsafe file or folder name",
  archive_text: "is a text file with unexpected characters",
};
const WHOLE = {
  archive_mac_metadata: "The ZIP was made with Finder Compress (it carries hidden Mac files). Pack it with your usual tool.",
  archive_too_many_files: "The ZIP has too many files (512 at most).",
  archive_size: "The ZIP is empty or larger than 50 MB.",
  archive_timeout: "The check took too long. Press Check again.",
  archive_resource_limit: "The ZIP is too large to check in one go. Split it or make it smaller.",
};
const day = (at) => new Date(at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
const mb = (size) => (size / 1024 / 1024).toFixed(1) + " MB";
export function checkWords(v) {
  if (!v) return "No file yet";
  if (v.state === "passed") return "Passed the check";
  if (v.state === "waiting") return "Checking…";
  if (v.state === "retry") return "The check took too long. Press Check again.";
  if (WHOLE[v.reason]) return "Refused — " + WHOLE[v.reason];
  return `Refused — ${v.entry || "a file in the ZIP"} ${REASONS[v.reason] || "could not be accepted"}`;
}

function Summary() {
  const state = useRemote("/admin/paperwork/editions/summary");
  return <Loading state={state}>{state.data && <p className="aw-notice" role="status">Delivered from editions: {state.data.published} of {state.data.total} Basic ·{" "}
    {state.data.started} started, not published · {state.data.notStarted} not started. Orders for the others are prepared by hand within 5 business days.</p>}</Loading>;
}

function Grid() {
  const [truck, setTruck] = useState("box-truck"), [arrangement, setArrangement] = useState("w2");
  const state = useRemote(`/admin/paperwork/editions?equipment=${truck}&arrangement=${arrangement}`, 0, { keep: true });
  return <>
    <div className="aw-row">
      <label>Truck<select value={truck} onChange={(e) => setTruck(e.target.value)}>{TRUCKS.map(([id, l]) => <option key={id} value={id}>{l}</option>)}</select></label>
      <label>Arrangement<select value={arrangement} onChange={(e) => setArrangement(e.target.value)}>{ARRANGEMENTS.map(([id, l]) => <option key={id} value={id}>{l}</option>)}</select></label>
    </div>
    <Loading state={state}>{state.data && <table className="aw-table">
      <thead><tr><th>State</th><th>Folder</th><th>What it covers</th><th>Latest file</th><th>Published</th><th /></tr></thead>
      <tbody>{state.data.rows.map((r) => <tr key={r.id}>
        <td>{r.stateLabel}</td><td>{r.folderKey}</td><td>{r.hasText ? "Written" : "Missing"}</td><td>{checkWords(r.latest)}</td>
        <td>{r.published ? `Published ${day(r.published.at)}` : "Not published"}</td>
        <td><Link className="aw-button aw-small" to={`/admin/paperwork/${r.id}`}>Open</Link></td>
      </tr>)}</tbody>
    </table>}</Loading>
  </>;
}

function EditionPage({ id }) {
  const [tick, setTick] = useState(0), state = useRemote(`/admin/paperwork/editions/${id}`, tick, { keep: true });
  const [text, setText] = useState(null), [busy, setBusy] = useState(""), [error, setError] = useState(null), [notice, setNotice] = useState("");
  const [chosen, setChosen] = useState(""), [checked, setChecked] = useState(false), [reason, setReason] = useState("correction");
  const keys = useRef({});
  const key = (tag) => (keys.current[tag] ||= operationKey());
  const done = (tag, message) => { delete keys.current[tag]; setNotice(message); setTick((n) => n + 1); };
  const run = async (tag, work) => {
    if (busy) return;
    setBusy(tag); setError(null); setNotice("");
    try { await work(); } catch (e) { setError(e); } finally { setBusy(""); }
  };
  const d = state.data, passed = d ? d.versions.filter((v) => v.state === "passed") : [];
  const version = passed.find((v) => v.versionId === chosen) || passed[0];
  const saveText = () => run("text", async () => {
    await adminRequest(`/admin/paperwork/editions/${id}/text`, { method: "PUT", key: key("text"), body: { expectedRevision: d.revision, coversText: text } });
    setText(null); done("text", "Saved. The published text changes only when you publish again.");
  });
  const upload = (file) => run("upload", async () => {
    if (!file || !/\.zip$/i.test(file.name) || file.size < 1 || file.size > MAX_BYTES) throw Error("Choose one ZIP file of at most 50 MB.");
    setNotice("Reading the file…");
    const checksum = await hashFile(file);
    const begun = (await adminRequest(`/admin/paperwork/editions/${id}/uploads`, { method: "POST", key: key("upload:" + checksum),
      body: { fileName: `${d.folderKey}.zip`, mime: "application/zip", size: file.size, checksum } })).data;
    setNotice("Uploading…");
    const journal = createJournal(window.localStorage, "editions");
    const sent = await transfer({ upload: begun, file, journal });
    setNotice("Checking every file inside the ZIP…");
    await adminRequest(`/admin/paperwork/uploads/${encodeURIComponent(begun.uploadId)}/validate`, { method: "POST", key: key("check:" + begun.uploadId),
      body: { expectedRevision: sent.versionRevision } });
    done("upload:" + checksum, "Uploaded and checked. See the result below.");
  });
  const publish = () => run("publish", async () => {
    await adminRequest(`/admin/paperwork/editions/${id}/publish`, { method: "POST", key: key("publish"),
      body: { expectedRevision: d.revision, versionId: version.versionId, versionChecksum: version.file.checksum, confirmed: true } });
    setChecked(false); done("publish", "Published. New orders for this edition are prepared from this file.");
  });
  const unpublish = () => run("unpublish", async () => {
    await adminRequest(`/admin/paperwork/editions/${id}/unpublish`, { method: "POST", key: key("unpublish"), body: { expectedRevision: d.revision, reason, confirmed: true } });
    done("unpublish", "Unpublished. Orders for this edition are prepared by hand until you publish again.");
  });
  return <section className="aw-panel" aria-labelledby="edition-title">
    <p><Link to="/admin/paperwork">All editions</Link></p>
    <Loading state={state}>{d && <>
      <h2 id="edition-title">{d.label}</h2>
      <p className="aw-muted">Folder {d.folderKey} · {d.published ? `Published ${day(d.published.at)} · ${mb(d.published.file.size)}` : "Not published"}</p>
      <ErrorNotice error={error} />{notice && <p className="aw-notice" role="status">{notice}</p>}
      <fieldset className="aw-array" disabled={Boolean(busy)}><legend>What it covers</legend>
        <textarea rows={5} maxLength={4000} value={text ?? d.coversText} onChange={(e) => setText(e.target.value)} />
        <button className="aw-button" disabled={text === null || !text.trim()} onClick={saveText}>{busy === "text" ? "Saving…" : "Save text"}</button>
      </fieldset>
      <fieldset className="aw-array" disabled={Boolean(busy)}><legend>Upload a ZIP</legend>
        <p className="aw-muted">PDF, Word (.docx), Excel (.xlsx), CSV, TXT, PNG and JPG only. Every file inside is checked automatically.</p>
        <input type="file" accept=".zip,application/zip" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f); }} />
      </fieldset>
      <h3>Files</h3>
      {d.versions.length ? <ul className="aw-list">{d.versions.map((v) => <li key={v.versionId}>{day(v.uploadedAt)} · {mb(v.file.size)} · {checkWords(v)}
        {d.published?.versionId === v.versionId ? " · Published" : ""}</li>)}</ul> : <p className="aw-muted">No file uploaded yet.</p>}
      <fieldset className="aw-array" disabled={Boolean(busy) || !passed.length}><legend>Publish</legend>
        {passed.length ? <>
          <label>File<select value={version.versionId} onChange={(e) => setChosen(e.target.value)}>{passed.map((v) => <option key={v.versionId} value={v.versionId}>{day(v.uploadedAt)} · {mb(v.file.size)}</option>)}</select></label>
          <p className="aw-muted">Published text: {d.coversText || "write it above first"}</p>
          <label className="aw-check"><input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} /> I opened this ZIP and checked this text</label>
          <button className="aw-button aw-primary" disabled={!checked || !d.coversText} onClick={publish}>{busy === "publish" ? "Publishing…" : "Publish this edition"}</button>
        </> : <p className="aw-muted">Upload a ZIP that passes the check to publish this edition.</p>}
      </fieldset>
      {d.published && <fieldset className="aw-array" disabled={Boolean(busy)}><legend>Unpublish</legend>
        <label>Reason<select value={reason} onChange={(e) => setReason(e.target.value)}>
          <option value="owner_request">Owner request</option><option value="correction">Correction</option><option value="temporarily_unavailable">Temporarily unavailable</option></select></label>
        <button className="aw-button" onClick={unpublish}>{busy === "unpublish" ? "Working…" : "Unpublish"}</button>
      </fieldset>}
      {d.history.length > 0 && <><h3>History</h3><ul className="aw-list">{d.history.map((h) => <li key={h.n}>{day(h.at)} · {h.action === "publish" ? `Published (${mb(h.file.size)})` : `Unpublished — ${{ owner_request: "owner request", correction: "correction", temporarily_unavailable: "temporarily unavailable" }[h.reason]}`}</li>)}</ul></>}
    </>}</Loading>
  </section>;
}

export default function Editions({ id }) {
  return id ? <EditionPage id={id} /> : <section className="aw-panel" aria-labelledby="editions-title">
    <h2 id="editions-title">Paperwork editions</h2>
    <p className="aw-muted">Each Basic edition is one truck, arrangement and state. Paid orders for a published edition are prepared from its file in one click.</p>
    <Summary /><Grid />
  </section>;
}
